import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from 'react-timer-hook';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTimer as useAppTimer } from '../../../context/TimerContext';
import { TimerType } from '../../../shared/constants';
import BaseActionButton from '../../designe/ActionButtons/BaseActionButton';
import PlayActionButton from '../../designe/ActionButtons/PlayActionButton';
import RestartActionButton from '../../designe/ActionButtons/RestartActionButton';
import StopActionButton from '../../designe/ActionButtons/StopActionButton';
import { useStyles } from './AppTimer.styles';
import { CircularTimerSvg } from './CircularTimerSvg';

export interface IAppTimerProps {
  startTime: number;
  id: string;
  label?: string;
  onFinish?: (elapsedTime: number) => void;
  onClose?: () => void;
  autoStart?: boolean;
  showRestartButton?: boolean;
  showStopButton?: boolean;
  startValue?: number;
}
const MAX_TIMER_SECONDS = 59 * 60 + 45; // 59:45

function AppTimer({
  startTime,
  id: timerId,
  onFinish = () => {},
  autoStart = false,
  showRestartButton = true,
  showStopButton = true,
}: IAppTimerProps) {
  const { getTimer, saveTimer, deleteTimer } = useAppTimer();
  const [actualStartTime, setActualStartTime] = useState(startTime);
  const [expiryTimestamp, setExpiryTimestamp] = useState(getExpiryTimestamp());
  const { t } = useTranslation();

  const {
    totalSeconds,
    seconds,
    minutes,
    hours,
    isRunning,
    start,
    pause,
    resume,
    restart,
  } = useTimer({
    expiryTimestamp,
    autoStart,
    onExpire: () => {
      deleteTimer(timerId);
      pause();
      onFinish(actualStartTime);
    },
  });

  useEffect(() => {
    const totalSecondsNewValue = timerId ? totalSeconds : actualStartTime;
    const expiryTimestamp = getExpiryTimestamp();
    setExpiryTimestamp(expiryTimestamp);

    if (autoStart) {
      saveTimer(timerId, {
        type: TimerType.time,
        timestamp: new Date().valueOf() + totalSecondsNewValue * 1000,
        startTime: actualStartTime,
      });
      restart(expiryTimestamp);
    } else {
      pause();
      saveTimer(timerId, {
        type: TimerType.time,
        value: totalSecondsNewValue,
        startTime: actualStartTime,
      });
    }
  }, [timerId]);

  const theme = useTheme();

  const { classes, cx } = useStyles(theme);

  function getTimeView(): React.ReactElement {
    return (
      <>
        {hours > 0 && (
          <span>
            {('0' + hours).slice(-2)}
            {t('common.hShort')}
          </span>
        )}
        {minutes > 0 && (
          <span>
            {('0' + minutes).slice(-2)}
            {t('common.minShort')}
          </span>
        )}
        <span>
          {('0' + seconds).slice(-2)}
          {t('common.secShort')}
        </span>
      </>
    );
  }

  function getExpiryTimestamp() {
    let value: number;
    const now = new Date();
    const timer = getTimer(timerId);

    if (timer && 'value' in timer) {
      const timerValue =
        typeof timer.value === 'number' &&
        !isNaN(timer.value) &&
        timer.value >= 0
          ? timer.value
          : actualStartTime;
      value = now.valueOf() + timerValue * 1000;
    } else if (timer && 'timestamp' in timer) {
      value =
        typeof timer.timestamp === 'number' && !isNaN(timer.timestamp)
          ? timer.timestamp
          : now.valueOf() + actualStartTime * 1000;
    } else {
      value = now.valueOf() + actualStartTime * 1000;
    }

    return new Date(value);
  }

  function onStartClick() {
    if (actualStartTime <= 0) {
      return;
    }

    const timer = getTimer(timerId);
    const _startTime =
      timer && Number.isInteger(timer.startTime)
        ? timer.startTime
        : actualStartTime;
    if (isRunning) {
      pause();
      saveTimer(timerId, {
        type: TimerType.time,
        value: totalSeconds,
        startTime: _startTime,
      });
      setActualStartTime(_startTime);
      return;
    }

    saveTimer(timerId, {
      type: TimerType.time,
      timestamp: new Date().valueOf() + totalSeconds * 1000,
      startTime: _startTime,
    });
    setActualStartTime(_startTime);
    if (timer && 'timestamp' in timer) resume();

    if (!timer || (timer && 'value' in timer && timer?.value !== undefined))
      start();
  }

  function onStopClick() {
    const timer = getTimer(timerId);

    if (!timer) return;
    pause();
    deleteTimer(timerId);
    const elapsedTime = Math.max(0, (timer.startTime || 0) - totalSeconds);
    onFinish(elapsedTime);
  }

  function onRestartClick() {
    const timer = getTimer(timerId);
    const _startTime =
      timer && Number.isInteger(timer.startTime)
        ? timer.startTime
        : actualStartTime;
    const newTimestamp = new Date().valueOf() + _startTime * 1000;
    restart(new Date(newTimestamp), isRunning);
    saveTimer(timerId, {
      type: TimerType.time,
      timestamp: newTimestamp,
      startTime: _startTime,
    });
    setActualStartTime(_startTime);
  }

  function onAddSecondsClick(value: number) {
    const timer = getTimer(timerId);
    const _startTime =
      timer && Number.isInteger(timer.startTime)
        ? timer.startTime
        : actualStartTime;

    const newTotalSeconds = Math.max(
      1,
      Math.min(MAX_TIMER_SECONDS, totalSeconds + value),
    );
    const newStartTime = Math.max(
      1,
      Math.min(MAX_TIMER_SECONDS, _startTime + value),
    );
    const newtimestamp = new Date().valueOf() + newTotalSeconds * 1000;

    restart(new Date(newtimestamp), isRunning);
    saveTimer(timerId, {
      type: TimerType.time,
      timestamp: newtimestamp,
      startTime: newStartTime,
    });
    setActualStartTime(newStartTime);
  }

  const calculateOffset = () => {
    const safeStartTime = actualStartTime || 1;
    const progress = Math.max(0, Math.min(1, totalSeconds / safeStartTime));
    return 2 * Math.PI * 85 * (1 - progress);
  };

  return (
    <Box className={classes.timerContainer}>
      <Box className={classes.circularTimerContainer}>
        <Box className={classes.circularTimerSvgContainer}>
          <CircularTimerSvg offset={calculateOffset()} />
        </Box>
        <Box className={classes.timerValueContainer}>
          <Typography variant="h1">{getTimeView()}</Typography>
        </Box>
      </Box>
      <Box className={classes.buttonsPanel}>
        <BaseActionButton
          onClick={() => onAddSecondsClick(-15)}
          disabled={totalSeconds <= 15}>
          <Typography variant="text-md-sb">
            -15{t('common.secShort')}
          </Typography>
        </BaseActionButton>
        <PlayActionButton playing={isRunning} onClick={() => onStartClick()} />
        {showStopButton && <StopActionButton onClick={() => onStopClick()} />}
        {showRestartButton && (
          <RestartActionButton onClick={() => onRestartClick()} />
        )}
        <BaseActionButton
          onClick={() => onAddSecondsClick(15)}
          disabled={totalSeconds >= MAX_TIMER_SECONDS}>
          <Typography variant="text-md-sb">
            +15{t('common.secShort')}
          </Typography>
        </BaseActionButton>
      </Box>
    </Box>
  );
}

export default AppTimer;
